"use client"

import { useState, useEffect, useRef } from "react"

interface UserAvatarProps {
  image?: string | null
  name?: string | null
  size?: "xs" | "sm" | "md" | "lg" | "xl"
  className?: string
}

const sizeMap = {
  xs: "w-6 h-6 text-xs",
  sm: "w-8 h-8 text-sm",
  md: "w-10 h-10 text-base",
  lg: "w-12 h-12 text-lg",
  xl: "w-16 h-16 text-2xl",
}

export function UserAvatar({ image, name, size = "md", className = "" }: UserAvatarProps) {
  const sizeClasses = sizeMap[size]
  const [showImage, setShowImage] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  
  // Get initials from name (first letter of first and last name)
  const getInitials = (fullName?: string | null) => {
    if (!fullName) return "?"
    const parts = fullName.trim().split(/\s+/)
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase()
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
  }

  // Check image on mount and when image changes
  useEffect(() => {
    if (!image || image.trim() === "") {
      setShowImage(false)
      return
    }

    // Reset state
    setShowImage(false)

    // Create a new image to test loading
    const img = new Image()
    
    img.onload = () => {
      setShowImage(true)
    }
    
    img.onerror = () => {
      setShowImage(false)
    }
    
    img.src = image
    
    // If image is already complete (cached), set immediately
    if (img.complete) {
      setShowImage(true)
    }
  }, [image])

  // If no valid image URL or image failed to load, show initials
  if (!image || image.trim() === "" || !showImage) {
    return (
      <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold ${className}`}>
        {getInitials(name)}
      </div>
    )
  }

  // Show image
  return (
    <img
      ref={imgRef}
      src={image}
      alt={name || "Avatar"}
      className={`${sizeClasses} rounded-full object-cover ${className}`}
    />
  )
}
